import { useAutoTranslateMutation } from "entities/translations/mutations/mutateAutoTranslate";
import { useStaticTranslationsMutation } from "entities/translations/mutations/mutateStaticTranslations";
import { useStaticTranslationsQuery } from "entities/translations/queries/useStaticTranslations";
import { isEmpty } from "lodash";
import { TranslationTypes } from "modules/translations/types";
import {
  defineNoEnglishTranslate,
  getTotalUnpublishedChanges,
} from "modules/translations/utils";
import { FC, useState, PropsWithChildren, useCallback, useMemo } from "react";
import {
  TranslationError,
  type TranslatedContent,
} from "shared/api/openai/types";
import { useFilterApi } from "shared/hooks/useFilterApi";
import { LocaleCode } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import useTranslations from "../../../hooks/useTranslations";
import useFilteredData from "../hooks/useFilteredData";
import { useSearchFilterTranslations } from "../hooks/useSearchFilterTranslations";
import { StaticTranslationsTableData } from "../types";
import { StaticTranslationPageType, TranslationsDataResponse } from "./types";

const [Provider, useStaticTranslationPage] =
  createContext<StaticTranslationPageType>({
    name: "StaticTranslationsContext",
    hookName: "useStaticTranslationPage",
    providerName: "StaticTranslationsProvider",
  });

const StaticTranslationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: translationsList = [], isLoading } =
    useStaticTranslationsQuery();
  const { mutate: publishUpdatedChanges, isPending: isPendingPublish } =
    useStaticTranslationsMutation();
  const [hasShownTranslateMessageError, setShownTranslateMessageError] =
    useState(false);
  const [autoTranslateErrorMessage, setAutoTranslateErrorMessage] =
    useState<string>("");
  const [targetLanguage, setTargetLanguage] = useState<LocaleCode>();
  const { mutateAsync, isPending, isError } = useAutoTranslateMutation();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const { onSearchTranslation, searchValue, setSearchValue, isLoadingSearch } =
    useSearchFilterTranslations();

  const {
    filterData: areaFilterData,
    filteredValues: areaFilterValues,
    setFilteredValues: setAreaFilterValues,
  } = useFilterApi<StaticTranslationsTableData>(translationsList, "area");

  const {
    filterData: contentItemFilterData,
    filteredValues: contentItemFilterValues,
    setFilteredValues: setContentItemFilterValues,
  } = useFilterApi<StaticTranslationsTableData>(
    translationsList,
    "contentItem",
  );

  // Handle filtering
  const filteredData = useFilteredData({
    initialList: translationsList,
    searchValue,
    filterByContentItem: contentItemFilterValues,
    filterByArea: areaFilterValues,
  });

  const {
    handleUpdatedTranslations,
    setUpdatedTranslations,
    updatedTranslations,
  } = useTranslations<StaticTranslationsTableData>(
    filteredData,
    TranslationTypes.Static,
  );

  const onSuccessAutoTranslate = useCallback(
    (response: TranslationsDataResponse, lang: LocaleCode) => {
      if (!isError && !response.errorMessage && response.data !== null) {
        const translationArray = response.data;
        translationArray.forEach((translation: TranslatedContent) => {
          handleUpdatedTranslations({
            translationId: translation.id,
            value: translation[lang],
            locale: lang,
          });
        });
      } else {
        setShownTranslateMessageError(true);
        setAutoTranslateErrorMessage(
          (response as { errorMessage: string }).errorMessage,
        );
      }
    },
    [isError, handleUpdatedTranslations],
  );

  const handleAutoTranslate = async (lang: LocaleCode) => {
    const requestData = filteredData
      .slice(
        paginationModel.pageSize * paginationModel.page,
        paginationModel.pageSize * (paginationModel.page + 1),
      )
      .filter(
        ({ en, [lang]: translation, id }) =>
          en && !translation && !updatedTranslations?.[id],
      )
      .map(({ id, contentItem, en }) => ({
        id,
        content: contentItem,
        en: en || null,
      }));

    if (!requestData.length) {
      setShownTranslateMessageError(true);
      setAutoTranslateErrorMessage("There is no data to be autotranslated");
      return;
    }

    setTargetLanguage(lang);
    await mutateAsync(
      { langCode: lang, data: requestData },
      {
        onSuccess: (data) => onSuccessAutoTranslate(data, lang),
        onError: (error: TranslationError) => {
          if (error.errorMessage) {
            setShownTranslateMessageError(true);
            setAutoTranslateErrorMessage(error.errorMessage);
          }
        },
        onSettled: () => {
          setTargetLanguage(undefined);
        },
      },
    );
  };

  const handlePublishChanges = async () => {
    if (updatedTranslations) {
      publishUpdatedChanges(updatedTranslations, {
        onSuccess: () => setUpdatedTranslations(null),
      });
    }
  };

  const hasNoEnglishTranslate = defineNoEnglishTranslate(updatedTranslations);

  const totalAmountUnpublishedChanges =
    getTotalUnpublishedChanges(updatedTranslations);

  const isDisabledPublish =
    isEmpty(updatedTranslations) || Boolean(hasNoEnglishTranslate);

  const onClearFilterValues = () => {
    setSearchValue("");
    setAreaFilterValues([]);
    setContentItemFilterValues([]);
  };

  const isLoadingTable = isLoadingSearch || isLoading;

  const tableData = useMemo(
    () => ({
      data: filteredData,
      paginationModel,
      setPaginationModel,
    }),
    [filteredData, paginationModel],
  );

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    tableData,
    updatedTranslations,
    isAutoTranslationLoaded: isPending,
    hasShownTranslateMessageError,
    autoTranslateErrorMessage,
    handleUpdatedTranslations,
    isDisabledPublish,
    totalAmountUnpublishedChanges,
    handlePublishChanges,
    isPendingPublish,
    setShownTranslateMessageError,
    handleAutoTranslate,
    onSearchTranslation,
    searchValue,
    onClearFilterValues,
    areaFilterData,
    contentItemFilterData,
    isLoadingTable,
    targetLanguage,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { StaticTranslationsProvider, useStaticTranslationPage };
